import { useCallback } from "react";

import { convertCronExpression } from "components/FormFields/CronExpressionsField/helpers";
import KeyValue from "components/KeyValue";
import { Cross, Tick } from "components/icons/generated";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import CardCollapsibleWrapper from "ds/components/Card/Collapsible/components/Wrapper";
import CardCollapsibleHeader from "ds/components/Card/Collapsible/components/Header";
import CardCollapsibleContent from "ds/components/Card/Collapsible/components/Content";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import FeedbackActions from "ds/components/Feedback/Actions";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { SCHEDULING_TYPE } from "shared/Stack/Scheduling/types";
import { DriftDetectionIntegration, StackState } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import { StackContext } from "views/Stack/Context";
import CardContent from "ds/components/Card/components/Content";

import { StackSchedulingContextApi } from "../Context";
import { getNextSchedule } from "../helpers";
import { showManageDriftDetectionDeleteConfirmationModal } from "./DeleteConfirmationModal";
import { makeDriftDetectionRunsLink } from "./helpers";
import useDeleteDriftDetection from "./useDeleteDriftDetection";

type StackManageDriftDetectionListItemProps = {
  integration: DriftDetectionIntegration;
  stackId: string;
  hasPrivateWorkerPool: boolean;
  stackState?: StackState;
};

const StackManageDriftDetectionListItem = ({
  integration,
  stackId,
  hasPrivateWorkerPool,
  stackState,
}: StackManageDriftDetectionListItemProps) => {
  const [isCollapsed, toggleState] = useToggle(false);
  const toggle = useCallback(() => toggleState(), [toggleState]);
  const { canUpdateDriftDetection, canDeleteDriftDetection } = useTypedContext(StackContext);
  const { onEdit } = useTypedContext(StackSchedulingContextApi);

  const { deleteIntegration, loading: deletionLoading } = useDeleteDriftDetection(stackId);

  const handleDeleteConfirmation = useCallback(() => {
    showManageDriftDetectionDeleteConfirmationModal({
      onConfirm: deleteIntegration,
    });
  }, [deleteIntegration]);

  const handleEdit = useCallback(() => {
    onEdit(integration);
  }, [integration, onEdit]);

  const isBlocked =
    !hasPrivateWorkerPool || (stackState !== StackState.Finished && !integration.ignoreState);

  const driftDetectionRunsUrl = makeDriftDetectionRunsLink(stackId);

  return (
    <CardCollapsibleWrapper isCollapsed={isCollapsed} onToggle={toggle}>
      <CardCollapsibleHeader
        ariaLevel={3}
        ariaLabel="Drift Detection"
        suffix={
          (canUpdateDriftDetection.result ||
            canDeleteDriftDetection.result ||
            driftDetectionRunsUrl) && (
            <DropdownMenuEllipsis tooltip="Drift detection actions" buttonVariant="ghost">
              {driftDetectionRunsUrl && (
                <DropdownMenuItem href={driftDetectionRunsUrl}>
                  Show drift detection runs
                </DropdownMenuItem>
              )}
              {(canUpdateDriftDetection.result || !!canUpdateDriftDetection.message) && (
                <DropdownMenuItem
                  onAction={handleEdit}
                  isDisabled={!canUpdateDriftDetection.result}
                  tooltip={canUpdateDriftDetection.message}
                >
                  Edit
                </DropdownMenuItem>
              )}
              {(canDeleteDriftDetection.result || !!canDeleteDriftDetection.message) && (
                <DropdownMenuItem
                  isDisabled={!canDeleteDriftDetection.result}
                  tooltip={canDeleteDriftDetection.message}
                  loading={deletionLoading}
                  danger
                  onAction={handleDeleteConfirmation}
                  analyticsPage={AnalyticsPageStack.StackScheduling}
                  analyticsTitle="Schedule Delete Clicked"
                  analyticsProps={{ type: SCHEDULING_TYPE.DRIFT_DETECTION }}
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuEllipsis>
          )
        }
      >
        Drift Detection
      </CardCollapsibleHeader>

      {isBlocked && (
        <CardContent hasHeader>
          <Banner
            variant="danger"
            title="Drift Detection jobs for a given stack are not scheduling:"
            fullWidth
          >
            {!hasPrivateWorkerPool && (
              <>
                The stack uses an unsupported public worker pool.
                <br />
              </>
            )}
            {stackState !== StackState.Finished && !integration.ignoreState && (
              <>
                The stack is currently not in the stable (FINISHED) state. You can skip this check
                by changing drift detection to ignore stack state.
                <br />
              </>
            )}

            <FeedbackActions>
              <LinkButton
                variant="secondary"
                size="small"
                href={getDocsUrl("/concepts/stack/drift-detection")}
                rel="noopener noreferrer"
                target="_blank"
              >
                Read more
              </LinkButton>
            </FeedbackActions>
          </Banner>
        </CardContent>
      )}

      <CardCollapsibleContent hasHeader>
        {!isBlocked && (
          <KeyValue name="Starts in">
            {getNextSchedule(integration.nextSchedule || undefined)}
          </KeyValue>
        )}

        <KeyValue name="Scheduled">
          <Box direction="column">
            {integration.schedule.map((item, i, { length }) => (
              <Typography key={`${item}.${i}`} variant="p-body3" tag="span">
                {length > 1 && " •"} {convertCronExpression(item) ?? item}
              </Typography>
            ))}
          </Box>
        </KeyValue>

        <KeyValue name="Timezone">{integration.timezone}</KeyValue>

        <KeyValue name="Reconcile">
          {integration.reconcile ? (
            <Icon src={Tick} color="success" />
          ) : (
            <Icon src={Cross} color="danger" />
          )}
        </KeyValue>

        <KeyValue name="Ignore State">
          {integration.ignoreState ? (
            <Icon src={Tick} color="success" />
          ) : (
            <Icon src={Cross} color="danger" />
          )}
        </KeyValue>
      </CardCollapsibleContent>
    </CardCollapsibleWrapper>
  );
};

export default StackManageDriftDetectionListItem;
